import React from 'react'
import { PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'

import useLegalPages from 'common/providers/useLegalPages'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BsCaretLeftFill, BsCaretRightFill } from 'react-icons/bs'
import { AppState } from 'common/state-type'
import Constants from 'common/utils/constants'
import TermsAndPoliciesSubtitle from 'pages/terms-and-policies/TermsAndPoliciesSubtitle'

type TermsAndPoliciesListProps = {
  title?: string
}

const pageSize = 9

const TermsAndPoliciesList = ({
  title,
  children,
}: PropsWithChildren<TermsAndPoliciesListProps>) => {
  const { legalPages, getLegalPages } = useLegalPages()
  const [page, setPage] = useState(1)
  const isLoadingMore = useRef(false)

  const [previousData, setPreviousData] = useState<
    AppState['legalPages']['results']
  >([])

  const loadLegalPages = useCallback(() => {
    getLegalPages(
      {
        is_template: Constants.isAvvokaEnabled,
        page,
        page_size: pageSize,
      },
      {
        onSuccess: () => {
          isLoadingMore.current = false
        },
        onError: () => {
          isLoadingMore.current = false
        },
      },
    )
  }, [page, pageSize, getLegalPages])

  useEffect(loadLegalPages, [loadLegalPages])

  const swipeData = useCallback(
    (key) => () => {
      if (!legalPages?.[key] || isLoadingMore.current) {
        return
      }
      isLoadingMore.current = true
      key === 'next' ? setPage(page + 1) : setPage(page - 1)
    },
    [legalPages, page],
  )

  const loadMore = useCallback(() => {
    if (!legalPages?.next || isLoadingMore.current) {
      return
    }

    isLoadingMore.current = true
    setPreviousData([...previousData, ...(legalPages?.results ?? [])])
    setPage(page + 1)
  }, [legalPages, page])

  const results = useMemo(() => {
    const docs = [...previousData, ...(legalPages?.results ?? [])]

    // Check for uniqueness based on ID, just in case.
    return docs.filter(
      (value, index, self) =>
        index === self.findIndex(({ id }) => id === value.id),
    )
  }, [previousData, legalPages])

  return (
    <section className='px-[20px] md:px-[143px] md:py-[80px]'>
      <div className='max-sm:my-[100px]'>
        <TermsAndPoliciesSubtitle text='Our terms, policies, and disclaimers' />
        <div className='grid md:grid-cols-3 gap-[26px] justify-between md:my-[50px] my-[20px]'>
          {results.map((item, idx) => (
            <div
              key={idx}
              className='shadow-[0_.2px_4px_0_rgba(0,0,0,0.3)] p-[20px] rounded-md text-[#294B85] md:text-[#25395C] '
            >
              <Link
                to={{
                  pathname: `${item.slug}`,
                }}
                state={item}
                className='font-medium md:text-lg md:font-bold text-sm pt-[10px]'
              >
                {item.title}
              </Link>
              <p className='text-[#25395C] md:text-sm text-xs mt-[10px]'>
                {item.summary.substring(0, 150) + '...'}
              </p>
              <div className='flex justify-end '>
                <Link
                  to={{
                    pathname: `${item.slug}`,
                  }}
                  state={item}
                  className='btn btn-default mt-[20px] text-[#1C7D93]'
                >
                  View
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className='max-sm:hidden flex justify-end gap-5'>
          <BsCaretLeftFill
            className={`${
              !legalPages?.previous ? 'text-text-muted' : 'cursor-pointer'
            }`}
            onClick={swipeData('previous')}
            size={32}
          />
          <BsCaretRightFill
            className={`${
              !legalPages?.next ? 'text-text-muted' : 'cursor-pointer'
            }`}
            onClick={swipeData('next')}
            size={32}
          />
        </div>
        <div className='md:hidden flex justify-end'>
          <button className='underline' onClick={loadMore}>
            See more
          </button>
        </div>
      </div>
    </section>
  )
}

export default TermsAndPoliciesList
